import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import styles from './App.module.scss';

import languages from "./Assets/Language/Langloader";
import { ProvideAuth, useAuth } from "./Auth";
import LocaleHandler from "./LocaleHandler";
import Login from './components/Pages/Login/Login';
import Home from './components/Pages/Home/Home';
import { I18n } from 'react-polyglot';
import Header from './components/Shared/Header/Menu';
import { ProvideAxios } from './AxiosHandler';
import ErrorBoundary from './ErrorBoundary';
import Dogs from './components/Pages/Dogs/Dogs';
import Statistics from './components/Pages/Statistics/Statistics';
import Breedsimulator from './components/Pages/Dogs/Breedsimulator/Breedsimulator';
import Dog from './components/Pages/Dogs/Dog/Dog';
import Admin from './components/Pages/Admin/Admin';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import User from './components/Pages/Users/User';
import CreateUser from './components/Pages/Admin/Users/CreateUser';
import EditUser from './components/Pages/Admin/Users/EditUser';
import SchemaBreedReg from './components/PublicSchema/Schemas/SchemaBreedReg/SchemaBreedReg';
import SchemaMalePartnerReq from './components/PublicSchema/Schemas/SchemaMalePartnerReq/SchemaMalePartnerReq';
import SchemaSicknessReg from './components/PublicSchema/Schemas/SchemaSicknessReg/SchemaSicknessReg';
import BreedingPage from './components/Pages/BreedingDog/BreedingPage';
import SelectedBreedingDog from './components/Pages/BreedingDog/SelectedBreedingDog';
import RequestBreeding from './components/Pages/RequestBreeding/RequestBreeding';
import SelectedRequestBreeding from './components/Pages/RequestBreeding/SelectedRequestBreeding';
import SicknessReports from './components/Pages/SicknessReports/SicknessReports';
import SelectedSicknessReport from './components/Pages/SicknessReports/SelectedSicknessReport';
import SelectedOpenBreeding from './components/Pages/RequestBreeding/SelectedOpenBreeding';
import DynSchema from './components/PublicSchema/DynSchema/DynSchema';
import EditSchema from './components/Pages/Admin/Schemas/EditSchema/EditSchema';
import BreedingDogs from './components/PublicPages/BreedingDogs/BreedingDogs';
import BreedingPageConfig from './components/Pages/BreedingDog/BreedingPageConfig';
import PuppyListings from './components/PublicPages/PuppyListings/PuppyListings';
import RequestBreedingConfig from './components/Pages/RequestBreeding/RequestBreedingConfig';
import Privacy from './components/Pages/Privacy/Privacy';
import Userinvite from './components/PublicPages/Userinvite/Userinvite';
import EditPuppyListing from './components/PublicPages/EditPuppyListing/EditPuppyListing';
import ExternalLayout from './components/ExternalPortal/ExternalLayout';
import ExternalPortal from './components/ExternalPortal/ExternalPortal';
import ExternalBreedingDogs from './components/ExternalPortal/BreedingDogs/BreedingDogs';
import ExternalPuppyListings from './components/ExternalPortal/PuppyListings/PuppyListings';
import ExternalFaq from './components/ExternalPortal/Faq/ExternalFaq';
import ExternalStatistics from './components/ExternalPortal/Statistics/ExternalStatistics';

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

// Suppress react-polyglot defaultProps warning
const originalWarn = console.warn;
console.warn = (...args) => {
  if (args[0] && args[0].includes && args[0].includes('defaultProps will be removed from function components')) {
    return;
  }
  originalWarn.apply(console, args);
};

function PrivateRoute({ children }: React.PropsWithChildren<{}>): JSX.Element {
    const auth = useAuth();
    return <div>{
        auth.user != undefined && auth.user.token != "" ?
            <>
                <Header />
                {children}
            </>
        :
            <Navigate
                to={ "/login" + window.location.search }
                state={{ from: window.location.pathname }}
            />
    }</div>;
}

function ExternalRoute({ children }: React.PropsWithChildren<{}>): JSX.Element {
    const isProduction = window.location.hostname.includes('forvaltning.aninova.no');
    
    if (isProduction) {
        return <Navigate to="/login" replace />;
    }
    
    return <>{children}</>;
}

function App() {
    let localeTemp = "no";
    if (localStorage.getItem("user") != null) {
        let localUser = localStorage.getItem("user");
        if (localUser != null) {
            localUser = JSON.parse(localUser);
            if (localUser != null) {
                if ((localUser as any)["userdata"]["language"] != undefined) {
                    localeTemp = (localUser as any)["userdata"]["language"].toLowerCase();
                }
            }
        }
    }
    let languageTemp: any = {};
    languageTemp = languages(localeTemp);

    const [locale, setLocale] = React.useState(localeTemp);
    const [messages, setMessages] = React.useState(languageTemp);
    
    useEffect(
        () => {
            if (localStorage.getItem("user") != null) {
                let localUser = localStorage.getItem("user");
                if (localUser != null) {
                    localUser = JSON.parse(localUser);
                    if (localUser != null) {
                        if ((localUser as any)["userdata"]["language"] != undefined) {
                            setLocale((localUser as any)["userdata"]["language"].toLowerCase());
                        }
                    }
                }
            }
            setMessages(languages(locale));
        }, [locale]
    )

    return (
        <div className={styles.app}>
            <ErrorBoundary>
                <QueryClientProvider client={queryClient}>
                    <LocaleHandler.Provider value={{locale, setLocale}}>
                        <ProvideAuth>
                            <ProvideAxios>
                                <I18n locale={locale} messages={messages}>
                                <>
                                    <Router>
                                        <>
                                            <Routes>
                                                <Route path="/login" element={ <><Header /><Login /></> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/" element={ <PrivateRoute><Home /></PrivateRoute> }/>                                                <Route path="/statistics" element={ <PrivateRoute><Statistics /></PrivateRoute> }/>
                                                <Route path="/privacy" element={ <PrivateRoute><Privacy /></PrivateRoute> }/>
                                                <Route path="/admin" element={ <PrivateRoute><Admin /></PrivateRoute> }/>
                                                <Route path="/createuser/" element={ <PrivateRoute><CreateUser/></PrivateRoute> }/>
                                                <Route path="/edituser/:userid" element={ <PrivateRoute><EditUser/></PrivateRoute> }/>
                                                <Route path="/user/:userid" element={ <PrivateRoute><User /></PrivateRoute> }/>
                                                <Route path="/userinvite/:inviteid" element={ <Userinvite /> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/redigervalpeliste/:guid" element={ <EditPuppyListing /> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/skjema/avlshundreg/:breedid" element={ <SchemaBreedReg /> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/skjema/partnersoknad/:breedid" element={ <SchemaMalePartnerReq /> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/skjema/sykdomreg/:breedid" element={ <SchemaSicknessReg /> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/skjema/dyn/:breedid/:dynschemaid" element={ <DynSchema /> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/editdynschema/:dynschemaid" element={ <PrivateRoute><EditSchema /></PrivateRoute> }/>
                                                <Route path="/avlshunder/:breedid" element={ <BreedingDogs /> }/> {/* PUBLIC ROUTE */}
                                                <Route path="/valpelister/:breedid" element={ <PuppyListings /> }/> {/* PUBLIC ROUTE */}
                                                
                                                {/* External Portal Routes */}
                                                <Route path="/external" element={<ExternalLayout />}>
                                                    <Route index element={<ExternalPortal />} />
                                                    <Route path="breeding/:breedid?" element={<ExternalBreedingDogs />} />
                                                    <Route path="puppies" element={<ExternalPuppyListings />} />
                                                    <Route path="statistics" element={<ExternalStatistics />} />
<Route path="faq" element={<ExternalFaq />} />
                                                </Route>
                                                
                                                <Route path="/avlshundsoknader" element={ <PrivateRoute><BreedingPage /></PrivateRoute> }/>
                                                <Route path="/avlshundsoknader/config" element={ <PrivateRoute><BreedingPageConfig /></PrivateRoute> }/>
                                                <Route path="/avlshundsoknader/:schemakey/:breedregid" element={ <PrivateRoute><SelectedBreedingDog /></PrivateRoute> }/>
                                                <Route path="/partnersoknader" element={ <PrivateRoute><RequestBreeding /></PrivateRoute> }/>
                                                <Route path="/partnersoknader/config" element={ <PrivateRoute><RequestBreedingConfig /></PrivateRoute> }/>
                                                <Route path="/partnersoknader/:schemakey/:breedregid" element={ <PrivateRoute><SelectedRequestBreeding /></PrivateRoute> }/>
                                                <Route path="/sykdomsrapporter" element={ <PrivateRoute><SicknessReports /></PrivateRoute> }/>
                                                <Route path="/sykdomsrapporter/:schemakey/:breedregid" element={ <PrivateRoute><SelectedSicknessReport /></PrivateRoute> }/>
                                                <Route path="/openbreedings/:breedingid" element={ <PrivateRoute><SelectedOpenBreeding /></PrivateRoute> }/>
                                                <Route path="/hunder" element={ <PrivateRoute><Dogs /></PrivateRoute> }/>
                                                <Route path="/hunder/:dogid" element={ <PrivateRoute><Dog /></PrivateRoute> }/>
                                                <Route path="/hunder/paring/:dogid" element={ <PrivateRoute><Breedsimulator /></PrivateRoute> }/>
                                                <Route path="/:any" element={ <PrivateRoute></PrivateRoute> }/>
                                            </Routes>
                                            <ToastContainer
                                                autoClose={2500}
                                                position='bottom-right'
                                            />
                                        </>
                                    </Router>
                                </>
                            </I18n>
                        </ProvideAxios>
                    </ProvideAuth>
                </LocaleHandler.Provider>
                {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
            </QueryClientProvider>
        </ErrorBoundary>
    </div>
    );
}

export default App;